import cloneDeep from 'lodash/cloneDeep';
import { Signal } from 'typed-signals';
import type { XY } from 'utils/MathUtils';
import MathUtils from 'utils/MathUtils';
import { v4 } from 'uuid';
import type { BlockerMatchParams, BlockerModel, BlockerState, BlockerType, MeasureTapeBlockerState } from '../../types';
import type { TileStackModel } from '../board/TileStackModel';
import { BlockerConnection } from './BlockerConnection';

let instanceIndex = 0;

/**
 * Represents the state of a measure tape tile stack.
 * One measure tape blocker can be part of multiple chains.
 */
export type ChainState = {
  /**
   * array of TileStackModel representing the chain of tiles
   */
  tileStacks: TileStackModel[];

  /**
   * direction of the chain
   */
  direction: XY;

  /**
   * array of match ids that have been used to retract the chain
   * to prevent multiple retractions based on the same match
   */
  previousMatches: string[];
};

export class MeasureTapeBlockerModel implements BlockerModel {
  // eslint-disable-next-line no-plusplus
  public readonly id = `measureTape_${instanceIndex++}`;

  public readonly underTile = false;

  private readonly state: MeasureTapeBlockerState;

  public readonly connection: BlockerConnection;

  public readonly requestRemoval = new Signal();

  public readonly onModelChanged = new Signal();

  public readonly onRetract = new Signal<(direction: XY) => void>();

  public readonly destructible: boolean = true;

  public chainStates: ChainState[] = [];

  constructor(
    private readonly stack: TileStackModel,
    state: MeasureTapeBlockerState,
  ) {
    this.state = cloneDeep(state);
    this.connection = new BlockerConnection(this.stack, this.state);
    this.connection.onChange.connect(this.onConnectionChanged.bind(this));
  }

  public postLevelSetupInit(): void {
    if (!this.stack.blocker || !(this.stack.blocker instanceof MeasureTapeBlockerModel)) {
      console.error('MeasureTapeBlockerModel: cannot setup blocker chains, blocker not found');
      return;
    }

    // find all connected measure tape blockers to this blocker
    const connectedMeasureTapeStacks = this.stack.getConnectedBlockerStacks();

    // filter out only the measure tape blockers that are marked as cases
    const measureTapeCaseStacks = connectedMeasureTapeStacks.filter(
      (stack) => stack.blocker && stack.blocker instanceof MeasureTapeBlockerModel && stack.blocker.isCase,
    );

    // on these stacks order all connected measure tape stacks based on the direction into chains
    this.chainStates = measureTapeCaseStacks
      .map((caseStack) => this.findDirectionalBlockerChain(caseStack))
      .filter((chainState) => chainState.tileStacks.includes(this.stack));
  }

  private findDirectionalBlockerChain(stack: TileStackModel): ChainState {
    const chainState = {
      tileStacks: [stack],
      direction: { x: 0, y: 0 },
      previousMatches: [],
    };

    const connectedBlockerStacks = stack.getConnectedBlockerStacks();

    // remove case blocker
    connectedBlockerStacks.shift();

    // no connected blockers -> the chain is complete
    if (connectedBlockerStacks.length === 0) {
      return chainState;
    }

    // establish the direction of the chain
    chainState.tileStacks.push(connectedBlockerStacks[0]);
    chainState.direction = stack.getDirectionTo(connectedBlockerStacks[0]);

    connectedBlockerStacks.forEach((connectedStack, index) => {
      if (index === 0) return; // skip the first stack as it's already added

      const checkedStack = chainState.tileStacks[chainState.tileStacks.length - 1];

      // check if they are directly connected to each other
      if (!checkedStack.getConnectedBlockerStacks(true).includes(connectedStack)) return;

      const direction = checkedStack.getDirectionTo(connectedStack);

      if (MathUtils.comparePoints(direction, chainState.direction)) {
        chainState.tileStacks.push(connectedStack);
      }
    });

    return chainState;
  }

  public onMatch(config: BlockerMatchParams): void {
    const { matchId, stack, distantMatch } = config;
    if (!this.stack.isNeighborOf(stack) && !distantMatch) return;
    this.retractAll(matchId);
  }

  public retractAll(matchId: string = v4()): void {
    this.chainStates.forEach((chainState) => {
      if (!chainState.tileStacks.includes(this.stack)) {
        // this blocker is not part of the chain anymore since it was retracted
        // on delete will be called
        return;
      }

      // tile stacks of the chain are reduced by one when retracting
      // therefore use a copy of the array to iterate over
      [...chainState.tileStacks].forEach((stackToRetract) => {
        if (stackToRetract.blocker && stackToRetract.blocker instanceof MeasureTapeBlockerModel) {
          stackToRetract.blocker.retract(chainState.direction, matchId);
        }
      });
    });
  }

  private retract(direction: XY, matchId: string): void {
    const retractingChainState = this.chainStates.find((state) => MathUtils.comparePoints(state.direction, direction));
    if (!retractingChainState) {
      console.error(
        `MeasureTapeBlockerModel: no chain for direction ${direction.x},${direction.y}  at blocker ${this.id}, stack ${this.stack.x},${this.stack.y}`,
      );
      return;
    }

    if (retractingChainState.previousMatches.includes(matchId)) {
      // already retracted the chain based on this match
      // no further action needed
      return;
    }

    retractingChainState.previousMatches.push(matchId);
    retractingChainState.tileStacks.pop();

    this.onRetract.emit(retractingChainState.direction);

    if (!this.chainStates.some((chainState) => chainState.tileStacks.includes(this.stack))) {
      this.requestRemoval.emit();
    }
  }

  public onConnectionChanged(): void {
    this.onModelChanged.emit();
  }

  public get isCase(): boolean {
    return this.state.isCase;
  }

  public set isCase(isCase: boolean) {
    this.state.isCase = isCase;
    this.onModelChanged.emit();
  }

  public getType(): BlockerType {
    return this.state.type;
  }

  public exportState(): BlockerState {
    return this.state;
  }
}
